import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react'
import apiService from '../services/ApiService'
const CountryLawContext = createContext()
const countryDomainMap = {
  'uaetaxlaws.com': 'UAE',
  'www.uaetaxlaws.com': 'UAE',
  'ksataxlaws.com': 'KSA',
  'www.ksataxlaws.com': 'KSA',
  'kuwaittaxlaws.com': 'Kuwait',
  'www.kuwaittaxlaws.com': 'Kuwait',
  'qatartaxlaws.com': 'Qatar',
  'www.qatartaxlaws.com': 'Qatar',
  'omantaxlaws.com': 'Oman',
  'www.omantaxlaws.com': 'Oman',
  'bahraintaxlaws.com': 'Bahrain',
  'www.bahraintaxlaws.com': 'Bahrain',
  'gcctaxlaws.com': 'GCC',
  'www.gcctaxlaws.com': 'GCC',
}
export const CountryLawProvider = ({ children }) => {
  const [countriesWithLaws, setCountriesWithLaws] = useState([])

  // uae(are), bahrain, kuwait, oman, qatar, ksa, gcc, etc.
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const savedCountry = localStorage.getItem('selectedCountry')
    return savedCountry || '' // 'UAE' <-- countryName attribute used of the country object in all the sidebar filters, and same value we are sending to backend to fetch data
  })
  const [availableLaws, setAvailableLaws] = useState([]) // all laws of the selected country

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // this currentDomain state variable will only be useful to set laws for special domains like testgcctaxlaws.com and localhost, for other domains, the backend will automatically filter the data based on the domain of the request
  const [currentDomain, setCurrentDomain] = useState('') // uaetaxlaws.com, gcctaxlaws.com, etc.

  const [siteName, setSiteName] = useState('Tax Laws') // UAE Tax Laws, GCC Tax Laws, etc.
  const [countryFlag, setCountryFlag] = useState('') // ae, bh, kw, om, qa, sa

  const fetchCountriesWithLaws = useCallback(async () => {
    setLoading(true)
    try {
      // The server will automatically filter based on the domain of the request, only one country's data will be returned, testgcctaxlaws.com and localhost domains will return UAE data and for gcctaxlaws.com, all countries data will be returned

      // Get the current domain
      const domain = window.location.hostname
      setCurrentDomain(domain)

      // Get expected country name from domain mapping

      const expectedCountryName =
        countryDomainMap[domain] || countryDomainMap[`www.${domain}`]

      const countries = await apiService.getCountriesWithLaws()

      if (countries && Array.isArray(countries) && countries.length > 0) {
        setCountriesWithLaws(countries)

        let targetCountry = null

        // If we have an expected country from domain, try to find it

        if (expectedCountryName && expectedCountryName !== 'GCC') {
          targetCountry = countries.find(
            (c) =>
              c.countryName.toLowerCase() === expectedCountryName.toLowerCase()
          )
        }

        // If no target country found (or it's a GCC domain), use the first available country

        if (!targetCountry && countries.length > 0) {
          targetCountry = countries[0]
        }

        if (targetCountry) {
          // Set site name based on domain or target country

          if (expectedCountryName === 'GCC' || !expectedCountryName) {
            setSiteName('GCC Tax Laws')
          } else {
            setSiteName(`${expectedCountryName} Tax Laws`)
          }
          // Always use GCC flag for now
          setCountryFlag('GCC')
          // Set the selected country and available laws
          setSelectedCountry(targetCountry.countryName)
          setAvailableLaws(targetCountry.laws || [])
        } else {
          // Fallback if no countries available
          setSiteName('GCC Tax Laws')
          setCountryFlag('GCC')
          setSelectedCountry('')
          setAvailableLaws([])
        }
      } else {
        // No countries received
        setSiteName('GCC Tax Laws')
        setCountryFlag('GCC')
        setSelectedCountry('')
        setAvailableLaws([])
      }
      setError(null)
    } catch (err) {
      setError('Failed to load tax laws. Please try again later.')
      console.error('Error fetching countries with laws:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCountriesWithLaws()
  }, [fetchCountriesWithLaws])

  useEffect(() => {
    if (countriesWithLaws.length > 0 && selectedCountry) {
      const matchingCountry = countriesWithLaws.find(
        (c) => c.countryName.toLowerCase() === selectedCountry.toLowerCase()
      )

      if (matchingCountry && matchingCountry.laws) {
        setAvailableLaws(matchingCountry?.laws || [])
      } else {
        setAvailableLaws([])
      }
    }
  }, [selectedCountry, countriesWithLaws])

  useEffect(() => {
    localStorage.setItem('selectedCountry', selectedCountry)
  }, [selectedCountry])

  return (
    <CountryLawContext.Provider
      value={{
        countriesWithLaws,
        refreshCountriesData: fetchCountriesWithLaws,
        selectedCountry,
        setSelectedCountry,
        availableLaws, // all laws of all countries, possible only for gcctaxlaws.com domain, for other domains, only one country's data will be returned
        setAvailableLaws,
        currentDomain,
        siteName,
        countryFlag,
        loading,
        error,
      }}
    >
      {children}
    </CountryLawContext.Provider>
  )
}

export const useCountryLaw = () => {
  const context = useContext(CountryLawContext)
  if (context === undefined) {
    throw new Error('useCountry must be used within a CountryProvider')
  }
  return context
}
